import { Table, Thead, Tbody, Tr, Th, Td, chakra } from '@chakra-ui/react';
import { CaretUp, CaretDown } from '@phosphor-icons/react';
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  getSortedRowModel,
} from '@tanstack/react-table';
import { useState } from 'react';

type DataProps = Record<string, unknown>;

export type DataTableProps<Data extends DataProps> = {
  data: Data[];
  columns: ColumnDef<Data>[];
};

export function DataTable<Data extends DataProps>({
  data,
  columns,
}: DataTableProps<Data>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  return (
    <Table variant="striped">
      <Thead bg="greenBirch">
        {table.getHeaderGroups().map((headerGroup) => (
          <Tr key={headerGroup.id} textStyle="small">
            {headerGroup.headers.map((header) => {
              const { meta } = header.column.columnDef;
              return (
                <Th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  isNumeric={meta?.isNumeric}
                  color="black"
                  fontFamily="body"
                  fontSize="sm"
                  letterSpacing="initial"
                  textTransform="none"
                  fontWeight="normal"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}

                  {header.column.getIsSorted() && (
                    <chakra.span pl="4">
                      {header.column.getIsSorted() === 'desc' ? (
                        <CaretDown aria-label="sorted descending" />
                      ) : (
                        <CaretUp aria-label="sorted ascending" />
                      )}
                    </chakra.span>
                  )}
                </Th>
              );
            })}
          </Tr>
        ))}
      </Thead>
      <Tbody>
        {table.getRowModel().rows.map((row) => (
          <Tr key={row.id}>
            {row.getVisibleCells().map((cell) => {
              const { meta } = cell.column.columnDef;
              return (
                <Td key={cell.id} isNumeric={meta?.isNumeric}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              );
            })}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
